import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { bookingsApi } from '../../services/api';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Package, 
  Search,
  Loader2,
  Container,
  ArrowRight,
  FileText,
  Download,
  Filter,
  Calendar,
  MapPin,
  Users,
  X,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Pencil,
  ClipboardList
} from 'lucide-react';
import { formatDate, formatCurrency, attachmentFilename } from '../../lib/utils';
import { cargoLabel } from '../../components/booking/BulkFields';
import { EtaDialog } from '../../components/bridge/EtaDialog';
import { EtaDisplay } from '../../components/shared/EtaDisplay';
import { toast } from 'sonner';

function AgentBookings() {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';
  
  const [bookings, setBookings] = useState([]);
  const [agents, setAgents] = useState([]);
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingBL, setDownloadingBL] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [etaBooking, setEtaBooking] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [originFilter, setOriginFilter] = useState('all');
  const [destinationFilter, setDestinationFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(function() {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [bookingsRes, portsRes] = await Promise.all([
        bookingsApi.getAll(),
        api.get('/ports')
      ]);
      setBookings(bookingsRes.data);
      setPorts(portsRes.data);
      
      // Load agents only for admin
      if (isAdmin) {
        const agentsRes = await api.get('/admin/agents');
        setAgents(agentsRes.data);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusUpdate(bookingId, newStatus) {
    try {
      await bookingsApi.updateStatus(bookingId, newStatus);
      toast.success('Status updated successfully');
      loadData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  }

  async function handleDownloadBL(bookingId, bookingNumber) {
    setDownloadingBL(bookingId);
    try {
      const response = await api.get('/bookings/' + bookingId + '/bill-of-lading', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachmentFilename(response, 'BL_' + bookingNumber + '.pdf'));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bill of Lading downloaded');
    } catch (err) {
      toast.error('Failed to generate Bill of Lading');
    } finally {
      setDownloadingBL(null);
    }
  }

  function clearFilters() {
    setSearchTerm('');
    setStatusFilter('all');
    setAgentFilter('all');
    setOriginFilter('all');
    setDestinationFilter('all');
    setDateFrom('');
    setDateTo('');
  }

  // Get unique origins and destinations from bookings
  const uniqueOrigins = [...new Set(bookings.map(function(b) { return b.origin_port.split(',')[0]; }))].sort();
  const uniqueDestinations = [...new Set(bookings.map(function(b) { return b.destination_port.split(',')[0]; }))].sort();

  // Filter bookings
  const filteredBookings = bookings.filter(function(booking) {
    // Search filter
    const matchesSearch = !searchTerm || 
      booking.booking_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.origin_port.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.destination_port.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    
    // Agent filter (admin only)
    const matchesAgent = agentFilter === 'all' || booking.agent_id === agentFilter;
    
    // Origin filter
    const matchesOrigin = originFilter === 'all' || booking.origin_port.split(',')[0] === originFilter;
    
    // Destination filter
    const matchesDestination = destinationFilter === 'all' || booking.destination_port.split(',')[0] === destinationFilter;
    
    // Date filter
    let matchesDate = true;
    if (dateFrom) {
      const bookingDate = new Date(booking.created_at);
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      matchesDate = matchesDate && bookingDate >= fromDate;
    }
    if (dateTo) {
      const bookingDate = new Date(booking.created_at);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      matchesDate = matchesDate && bookingDate <= toDate;
    }
    
    return matchesSearch && matchesStatus && matchesAgent && matchesOrigin && matchesDestination && matchesDate;
  });

  const statusCounts = {
    all: bookings.length,
    pending: bookings.filter(function(b) { return b.status === 'pending'; }).length,
    confirmed: bookings.filter(function(b) { return b.status === 'confirmed'; }).length,
    in_transit: bookings.filter(function(b) { return b.status === 'in_transit'; }).length,
    delivered: bookings.filter(function(b) { return b.status === 'delivered'; }).length,
    cancelled: bookings.filter(function(b) { return b.status === 'cancelled'; }).length
  };

  const activeFilterCount = [
    agentFilter !== 'all',
    originFilter !== 'all',
    destinationFilter !== 'all',
    dateFrom !== '',
    dateTo !== ''
  ].filter(Boolean).length;

  const basePath = isAdmin ? '/admin' : '/agent';

  return (
    <div className="space-y-6" data-testid="agent-bookings">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-[32px] sv-display text-ink">
            {isAdmin ? 'All Bookings' : 'Manage Bookings'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isAdmin ? 'View and manage all system bookings' : 'View and manage your customer bookings'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={function() { loadData(); }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Link to={basePath + "/walkin-booking"}>
            <Button variant="outline" className="rounded-sm border-blue-500 text-blue-600 hover:bg-blue-100">
              <Users className="mr-2 h-4 w-4" /> Walk-in Booking
            </Button>
          </Link>
          <Link to={basePath + "/containers"}>
            <Button className="bg-ink hover:bg-ink-800 text-white rounded-sm">
              <Container className="mr-2 h-4 w-4" /> Assign Containers
            </Button>
          </Link>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex flex-wrap gap-2">
        {['all', 'pending', 'confirmed', 'in_transit', 'delivered', 'cancelled'].map(function(status) {
          const isActive = statusFilter === status;
          const label = status === 'all' ? 'All' : status.replace('_', ' ');
          return (
            <button
              key={status}
              onClick={function() { setStatusFilter(status); }}
              className={"px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize " + (
                isActive 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {label} ({statusCounts[status]})
            </button>
          );
        })}
      </div>

      {/* Search & Filter Toggle */}
      <div className="bg-white rounded-sm shadow-card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search by booking number, origin, or destination..."
              className="pl-12 h-12 rounded-sm"
              value={searchTerm}
              onChange={function(e) { setSearchTerm(e.target.value); }}
            />
          </div>
          <Button
            variant="outline"
            className={"rounded-sm h-12 px-6 " + (showFilters ? 'bg-blue-100 border-blue-500' : '')}
            onClick={function() { setShowFilters(!showFilters); }}
          >
            <Filter className="h-5 w-5 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <Badge className="ml-2 bg-blue-500 text-white">{activeFilterCount}</Badge>
            )}
            {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
          </Button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Agent Filter (Admin only) */}
              {isAdmin && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Users className="h-4 w-4" /> Agent
                  </Label>
                  <Select value={agentFilter} onValueChange={setAgentFilter}>
                    <SelectTrigger className="h-11 rounded-sm">
                      <SelectValue placeholder="All Agents" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All agents</SelectItem>
                      {agents.map(function(agent) {
                        return (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.first_name} {agent.last_name} ({agent.company})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Origin Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Origin Port
                </Label>
                <Select value={originFilter} onValueChange={setOriginFilter}>
                  <SelectTrigger className="h-11 rounded-sm">
                    <SelectValue placeholder="All Origins" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All origins</SelectItem>
                    {uniqueOrigins.map(function(port) {
                      return (
                        <SelectItem key={port} value={port}>{port}</SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Destination Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Destination Port
                </Label>
                <Select value={destinationFilter} onValueChange={setDestinationFilter}>
                  <SelectTrigger className="h-11 rounded-sm">
                    <SelectValue placeholder="All Destinations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All destinations</SelectItem>
                    {uniqueDestinations.map(function(port) {
                      return (
                        <SelectItem key={port} value={port}>{port}</SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Date From */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Calendar className="h-4 w-4" /> Date From
                </Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={function(e) { setDateFrom(e.target.value); }}
                  className="h-11 rounded-sm"
                />
              </div>

              {/* Date To */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Calendar className="h-4 w-4" /> Date To
                </Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={function(e) { setDateTo(e.target.value); }}
                  className="h-11 rounded-sm"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Showing {filteredBookings.length} of {bookings.length} bookings
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:bg-red-50"
                onClick={clearFilters}
              >
                <X className="h-4 w-4 mr-1" />
                Clear All Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-sm shadow-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filteredBookings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Booking #</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Route</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Container</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Price</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">ETA</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Status</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Update status</th>
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Documents</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map(function(booking) {
                  const needsContainer = !booking.container_numbers || booking.container_numbers.length === 0;
                  return (
                    <tr key={booking.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className={"w-10 h-10 rounded-sm flex items-center justify-center " + (needsContainer && booking.status === 'pending' ? 'bg-amber-100' : 'bg-blue-100')}>
                            <Package className={"h-5 w-5 " + (needsContainer && booking.status === 'pending' ? 'text-amber-600' : 'text-blue-600')} />
                          </div>
                          <div>
                            <p className="sv-mono font-medium text-ink">{booking.booking_number}</p>
                            {booking.bl_number && <p className="text-xs sv-mono text-gray-600" data-testid={"agent-bl-" + booking.booking_number}>B/L {booking.bl_number}</p>}
                            <p className="text-xs text-gray-500">{formatDate(booking.created_at)}</p>
                            {booking.created_by_agent && (
                              <Badge variant="outline" className="text-xs mt-1">Walk-in</Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <p className="text-sm font-medium">{booking.origin_port.split(',')[0]}</p>
                        <p className="text-xs text-gray-500">→ {booking.destination_port.split(',')[0]}</p>
                      </td>
                      <td className="py-4 px-6">
                        <p className="text-sm">{cargoLabel(booking)}</p>
                        {needsContainer && booking.status === 'pending' && (
                          <Link to={basePath + "/containers?booking=" + booking.id} className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                            Needs assignment <ArrowRight className="h-3 w-3" />
                          </Link>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <p className="font-medium text-ink">{formatCurrency(booking.price)}</p>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <EtaDisplay booking={booking} className="text-sm" testId={"agent-eta-" + booking.booking_number} />
                          {!['delivered', 'cancelled'].includes(booking.status) && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-sm text-gray-500 hover:text-ink" title="Revise ETA" onClick={function () { setEtaBooking(booking); }} data-testid={"agent-edit-eta-" + booking.booking_number}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <Badge className={"status-badge status-" + booking.status}>
                          {booking.status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-4 px-6">
                        <Select 
                          value={booking.status} 
                          onValueChange={function(value) { handleStatusUpdate(booking.id, value); }}
                        >
                          <SelectTrigger className="w-[140px] rounded-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="in_transit">In transit</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          {booking.status !== 'pending' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full"
                              onClick={function() { handleDownloadBL(booking.id, booking.booking_number); }}
                              disabled={downloadingBL === booking.id}
                              title="Download Bill of Lading"
                              data-testid={"agent-download-bl-" + booking.booking_number}
                            >
                              {downloadingBL === booking.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <FileText className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          {booking.status !== 'cancelled' && (
                            <Link to={basePath + "/bookings/" + booking.id + "/shipping-instructions"} data-testid={"agent-si-" + booking.booking_number}>
                              <Button variant={booking.shipping_instructions ? 'ghost' : 'outline'} size="sm" className={"rounded-full " + (booking.shipping_instructions ? '' : 'border-amber-400 text-amber-800 hover:bg-amber-50')} title={booking.shipping_instructions ? 'Shipping instructions submitted' : 'Shipping instructions required for the B/L'}>
                                <ClipboardList className="h-4 w-4" />
                              </Button>
                            </Link>
                          )}
                          <Link to={basePath + "/documents"} state={{ bookingId: booking.id }}>
                            <Button variant="ghost" size="sm" className="rounded-sm" title="Carrier documents">
                              <Download className="h-4 w-4" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-16">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No bookings found</p>
            {activeFilterCount > 0 && (
              <Button
                variant="link"
                className="text-blue-600 mt-2"
                onClick={clearFilters}
              >
                Clear filters to see all bookings
              </Button>
            )}
          </div>
        )}
      </div>
      <EtaDialog booking={etaBooking} open={Boolean(etaBooking)} onOpenChange={function (o) { if (!o) setEtaBooking(null); }} onUpdated={function (updated) { setBookings(function (prev) { return prev.map(function (b) { return b.id === updated.id ? { ...b, ...updated } : b; }); }); }} />
    </div>
  );
}

export default AgentBookings;
